import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
  TournamentOverData,
  TournamentOverDrawData,
  NextRoundData,
  ByeMessage,
  CancelledTournament,
  LobbyUpdateData
} from '@/shared/types';
import type { RootState } from '../index';

// Tournament interface (extend as needed)
export interface Tournament {
  id: number;
  name?: string;
  status: 'waiting' | 'active' | 'finished' | 'cancelled';
  currentRound?: number;
  totalRounds?: number;
}

interface TournamentsState {
  byId: { [tournamentId: number]: Tournament };
  allIds: number[];
  activeTournamentId: number | null;
  tournamentOverData: TournamentOverData | null;
  tournamentOverDrawData: TournamentOverDrawData | null;
  nextRoundMessage: NextRoundData | null;
  byeMessage: ByeMessage | null;
  cancelledTournament: CancelledTournament | null;
  lobbyUpdateData: LobbyUpdateData | null;
}

const initialState: TournamentsState = {
  byId: {},
  allIds: [],
  activeTournamentId: null,
  tournamentOverData: null,
  tournamentOverDrawData: null,
  nextRoundMessage: null,
  byeMessage: null,
  cancelledTournament: null,
  lobbyUpdateData: null
};

const tournamentsSlice = createSlice({
  name: 'tournaments',
  initialState,
  reducers: {
    // Tournament created/updated
    tournamentUpserted: (state, action: PayloadAction<Tournament>) => {
      const id = action.payload.id;
      state.byId[id] = action.payload;
      if (!state.allIds.includes(id)) {
        state.allIds.push(id);
      }
    },

    // Set active tournament
    setActiveTournament: (state, action: PayloadAction<number | null>) => {
      state.activeTournamentId = action.payload;
    },

    // Tournament over
    tournamentOver: (state, action: PayloadAction<TournamentOverData>) => {
      state.tournamentOverData = action.payload;

      // Update tournament status
      if (action.payload.tournamentId && state.byId[action.payload.tournamentId]) {
        state.byId[action.payload.tournamentId].status = 'finished';
      }
    },

    // Tournament over with draw
    tournamentOverDraw: (state, action: PayloadAction<TournamentOverDrawData>) => {
      state.tournamentOverDrawData = action.payload;

      // Update tournament status
      if (action.payload.tournamentId && state.byId[action.payload.tournamentId]) {
        state.byId[action.payload.tournamentId].status = 'finished';
      }
    },

    // Next round message
    nextRound: (state, action: PayloadAction<NextRoundData>) => {
      state.nextRoundMessage = action.payload;

      // Update tournament round if ID exists (Note: NextRoundData doesn't have round field in the type)
      if (action.payload.tournamentId && state.byId[action.payload.tournamentId]) {
        // Round info would need to be parsed from message or added to type
      }
    },

    // Bye message
    byeReceived: (state, action: PayloadAction<ByeMessage>) => {
      state.byeMessage = action.payload;
    },

    // Tournament cancelled
    tournamentCancelled: (state, action: PayloadAction<CancelledTournament>) => {
      state.cancelledTournament = action.payload;

      // Update tournament status (Note: CancelledTournament uses tournamentID with capital ID)
      if (action.payload.tournamentID && state.byId[action.payload.tournamentID]) {
        state.byId[action.payload.tournamentID].status = 'cancelled';
      }
    },

    lobbyUpdated: (state, action: PayloadAction<LobbyUpdateData>) => {
      state.lobbyUpdateData = action.payload;
    },

    // Clear messages after processing
    clearTournamentOverData: (state) => {
      state.tournamentOverData = null;
    },

    clearTournamentOverDrawData: (state) => {
      state.tournamentOverDrawData = null;
    },

    clearNextRoundMessage: (state) => {
      state.nextRoundMessage = null;
    },

    clearByeMessage: (state) => {
      state.byeMessage = null;
    },

    clearCancelledTournament: (state) => {
      state.cancelledTournament = null;
    },

    clearLobbyUpdateData: (state) => {
      state.lobbyUpdateData = null;
    },

    // Clear all tournaments
    clearAllTournaments: (state) => {
      state.byId = {};
      state.allIds = [];
      state.activeTournamentId = null;
      state.tournamentOverData = null;
      state.tournamentOverDrawData = null;
      state.nextRoundMessage = null;
      state.byeMessage = null;
      state.cancelledTournament = null;
      state.lobbyUpdateData = null;
    }
  }
});

export const {
  tournamentUpserted,
  setActiveTournament,
  tournamentOver,
  tournamentOverDraw,
  nextRound,
  byeReceived,
  tournamentCancelled,
  lobbyUpdated,
  clearTournamentOverData,
  clearTournamentOverDrawData,
  clearNextRoundMessage,
  clearByeMessage,
  clearCancelledTournament,
  clearLobbyUpdateData,
  clearAllTournaments
} = tournamentsSlice.actions;

// Selectors
export const selectAllTournaments = (state: RootState) =>
  state.tournaments.allIds.map(id => state.tournaments.byId[id]);

export const selectTournamentById = (state: RootState, tournamentId: number) =>
  state.tournaments.byId[tournamentId];

export const selectActiveTournamentId = (state: RootState) => state.tournaments.activeTournamentId;
export const selectActiveTournament = (state: RootState) =>
  state.tournaments.activeTournamentId
    ? state.tournaments.byId[state.tournaments.activeTournamentId]
    : null;
export const selectTournamentOverData = (state: RootState) => state.tournaments.tournamentOverData;
export const selectTournamentOverDrawData = (state: RootState) => state.tournaments.tournamentOverDrawData;
export const selectNextRoundMessage = (state: RootState) => state.tournaments.nextRoundMessage;
export const selectByeMessage = (state: RootState) => state.tournaments.byeMessage;
export const selectCancelledTournament = (state: RootState) => state.tournaments.cancelledTournament;
export const selectLobbyUpdateData = (state: RootState) => state.tournaments.lobbyUpdateData;

export default tournamentsSlice.reducer;
